import type * as Highcharts from 'highcharts'

import type { Sentiment } from '../api/types'

export type PointClickHandler = (params: { categoryId: number; sentiment: Sentiment | null }) => void

export type SelectionState = {
	categoryId: number | null
	sentiment: Sentiment | null
}

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

function translateSentiment(sentiment: Sentiment): string {
	const key = `core.blocks.text_analytics.sentiment.${sentiment}`
	return window.appConfig.translations[key] ?? sentiment
}

// ---------------------------------------------------------------------------
// Snapshot click handler
// Reads categoryIds from chart.userOptions (set as a custom top-level key by PHP).
// ---------------------------------------------------------------------------

export function buildSnapshotClickHandler(onPointClick: PointClickHandler): (this: Highcharts.Point) => void {
	return function (this: Highcharts.Point) {
		const chart = this.series.chart
		const userOpts = chart.userOptions as Highcharts.Options & {
			categoryIds?: number[]
		}
		const categoryId = userOpts.categoryIds?.[this.index] ?? null
		if (categoryId === null) {
			return
		}

		// Determine sentiment from series name when sentiment breakup is enabled.
		// sentimentBreakupEnabled is not available as a plain JSON field once options come
		// from BaseGraph (it was a ChartOptionsPayload field). We detect it by checking
		// whether the legend is enabled: when legend is enabled, sentiment breakup is on.
		const legendEnabled = (chart.userOptions.legend as { enabled?: boolean } | undefined)?.enabled === true
		const sentiment = extractSentimentFromSeriesName(this.series.name, legendEnabled)

		onPointClick({ categoryId, sentiment })
	}
}

// ---------------------------------------------------------------------------
// Trend click handler
// Reads categoryId from series.userOptions (set per-series by PHP).
// ---------------------------------------------------------------------------

export function buildTrendClickHandler(onPointClick: PointClickHandler): (this: Highcharts.Point) => void {
	return function (this: Highcharts.Point) {
		const sOpts = this.series.userOptions as Highcharts.SeriesOptions & {
			categoryId?: number
		}
		const categoryId = sOpts.categoryId ?? null
		if (categoryId === null) {
			return
		}
		onPointClick({ categoryId, sentiment: null })
	}
}

// ---------------------------------------------------------------------------
// Snapshot mouseOut / load handlers
// ---------------------------------------------------------------------------

export function buildMouseOutHandler(getSelection: () => SelectionState): (event: Event) => void {
	return function (event: Event): void {
		const selection = getSelection()
		if (selection.categoryId === null) {
			return
		}
		const isSentimentSelected = selection.sentiment !== null
		const sentimentName =
			isSentimentSelected && selection.sentiment !== null ? translateSentiment(selection.sentiment) : null

		const ev = event as { target?: Highcharts.Point }
		const target = ev.target
		if (target === undefined || target === null) {
			return
		}
		const chart = target.series?.chart
		if (chart === undefined) {
			return
		}
		const userOpts = chart.userOptions as Highcharts.Options & {
			categoryIds?: number[]
		}
		chart.series.forEach((s) => {
			s.points
				.filter((p) => {
					if (isSentimentSelected && sentimentName !== null && p.series.name !== sentimentName) {
						return true
					}
					return (userOpts.categoryIds?.[p.index] ?? null) !== selection.categoryId
				})
				.forEach((p) => {
					if (p === target) {
						setTimeout(() => p.setState('inactive'), 1)
					} else {
						p.setState('inactive')
					}
				})
		})
	}
}

export function buildLoadHandler(getSelection: () => SelectionState): (event: Event) => void {
	return function (event: Event): void {
		const selection = getSelection()
		if (selection.categoryId === null) {
			return
		}
		const isSentimentSelected = selection.sentiment !== null
		const sentimentName =
			isSentimentSelected && selection.sentiment !== null ? translateSentiment(selection.sentiment) : null

		const target = event.target as (Highcharts.Chart & { series?: Highcharts.Series[] }) | null
		if (target === null) {
			return
		}
		const userOpts = target.userOptions as Highcharts.Options & {
			categoryIds?: number[]
		}

		setTimeout(() => {
			target.series?.forEach((s) => {
				s.points
					.filter((p) => {
						if (isSentimentSelected && sentimentName !== null && p.series.name !== sentimentName) {
							return true
						}
						return (userOpts.categoryIds?.[p.index] ?? null) !== selection.categoryId
					})
					.forEach((p) => p.setState('inactive'))
			})
		}, 1)
	}
}

// ---------------------------------------------------------------------------
// Trend mouseOut handlers (point-level and series-level)
// ---------------------------------------------------------------------------

export function buildTrendPointMouseOutHandler(getSelection: () => SelectionState): (event: Event) => void {
	return function (event: Event): void {
		const selection = getSelection()
		if (selection.categoryId === null) {
			return
		}

		const ev = event as { target?: Highcharts.Point }
		const target = ev.target
		if (target === undefined || target === null || target.series === undefined) {
			return
		}

		const targetPoint = target
		target.series.chart.series
			.filter((s) => {
				const sOpts = s.userOptions as Highcharts.SeriesOptions & {
					categoryId?: number
				}
				return sOpts.categoryId !== selection.categoryId
			})
			.forEach((s) => {
				setTimeout(() => s.group?.attr({ opacity: 0.2 }), 1)
				s.points.forEach((p) => {
					if (p === targetPoint) {
						setTimeout(() => p.graphic?.attr({ opacity: 0.2 }), 1)
					} else {
						p.graphic?.attr({ opacity: 0.2 })
					}
				})
			})
	}
}

export function buildTrendSeriesMouseOutHandler(getSelection: () => SelectionState): (event: Event) => void {
	return function (event: Event): void {
		const selection = getSelection()
		if (selection.categoryId === null) {
			return
		}

		const ev = event as { target?: Highcharts.Series }
		const target = ev.target
		if (target === undefined || target === null) {
			return
		}

		const targetSeries = target
		target.chart.series
			.filter((s) => {
				const sOpts = s.userOptions as Highcharts.SeriesOptions & {
					categoryId?: number
				}
				return sOpts.categoryId !== selection.categoryId
			})
			.forEach((s) => {
				if (s === targetSeries) {
					setTimeout(() => s.group?.attr({ opacity: 0.2 }), 1)
				} else {
					s.group?.attr({ opacity: 0.2 })
				}
			})
	}
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const SENTIMENT_KEYS: Array<{ key: string; value: Sentiment }> = [
	{ key: 'core.blocks.text_analytics.sentiment.positive', value: 'positive' },
	{ key: 'core.blocks.text_analytics.sentiment.negative', value: 'negative' },
	{ key: 'core.blocks.text_analytics.sentiment.neutral', value: 'neutral' },
]

function extractSentimentFromSeriesName(name: string, enabled: boolean): Sentiment | null {
	if (!enabled) {
		return null
	}
	for (const { key, value } of SENTIMENT_KEYS) {
		const label = window.appConfig.translations[key]
		if (label !== undefined && name === label) {
			return value
		}
	}
	return null
}
